import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Settings, Clock, Target, Mail, MessageCircle, Phone, Users, TrendingUp, CheckCircle, Edit3, Copy, Plus, BarChart3, Bot } from 'lucide-react';

const AutomationPanel = ({ campaigns = [], onCampaignUpdate }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState('email-sequence');
  const [automationRules, setAutomationRules] = useState([]);
  const [activeAutomations, setActiveAutomations] = useState([]);
  const [workflowBuilder, setWorkflowBuilder] = useState(null);

  const workflowTemplates = [
    {
      id: 'email-sequence',
      name: 'Email Follow-up Sequence',
      description: 'Automated email sequence for nurturing leads through the sales funnel',
      type: 'email',
      duration: '14 days',
      steps: 7,
      successRate: 24,
      triggers: ['Form submission', 'Demo request', 'High lead score'],
      steps_detail: [
        { day: 0, type: 'email', subject: 'Welcome & Next Steps', delay: '0 hours' },
        { day: 1, type: 'email', subject: 'Case Study: Similar Company Success', delay: '24 hours' },
        { day: 3, type: 'email', subject: 'ROI Calculator & Pricing Info', delay: '72 hours' },
        { day: 5, type: 'call', subject: 'Personal Check-in Call', delay: '120 hours' },
        { day: 7, type: 'email', subject: 'Limited Time Offer', delay: '168 hours' },
        { day: 10, type: 'email', subject: 'Final Opportunity', delay: '240 hours' },
        { day: 14, type: 'sms', subject: 'Quick question about your needs', delay: '336 hours' }
      ]
    },
    {
      id: 'urgency-closer',
      name: 'Urgency-Based Closer',
      description: 'AI-powered urgency creation for high-intent leads ready to close',
      type: 'multi-channel',
      duration: '5 days',
      steps: 4,
      successRate: 42,
      triggers: ['Hot lead score (>80)', 'Demo completed', 'Pricing page visits'],
      steps_detail: [
        { day: 0, type: 'call', subject: 'Immediate follow-up call', delay: '2 hours' },
        { day: 1, type: 'email', subject: 'Limited availability this month', delay: '24 hours' },
        { day: 2, type: 'sms', subject: 'Quick question about timeline', delay: '48 hours' },
        { day: 5, type: 'email', subject: 'Final call - closing this opportunity', delay: '120 hours' }
      ]
    },
    {
      id: 'nurture-sequence',
      name: 'Long-term Nurture',
      description: 'Extended nurturing sequence for leads not ready to buy immediately',
      type: 'educational',
      duration: '90 days',
      steps: 12,
      successRate: 18,
      triggers: ['Cold lead score (<40)', 'No recent engagement', 'Future timeline'],
      steps_detail: [
        { day: 0, type: 'email', subject: 'Industry insights & trends', delay: '0 hours' },
        { day: 7, type: 'email', subject: 'Best practices guide', delay: '168 hours' },
        { day: 14, type: 'email', subject: 'Customer success story', delay: '336 hours' },
        { day: 21, type: 'email', subject: 'ROI analysis template', delay: '504 hours' },
        { day: 30, type: 'call', subject: 'Quarterly check-in call', delay: '720 hours' },
        { day: 45, type: 'email', subject: 'Product updates & roadmap', delay: '1080 hours' }
      ]
    },
    {
      id: 'objection-handler',
      name: 'Objection Handler',
      description: 'AI-powered sequence to address common objections and concerns',
      type: 'intelligent',
      duration: '7 days',
      steps: 5,
      successRate: 35,
      triggers: ['Price objection', 'Need to think about it', 'Budget concerns'],
      steps_detail: [
        { day: 0, type: 'email', subject: 'Addressing your concerns', delay: '4 hours' },
        { day: 1, type: 'email', subject: 'ROI proof & customer testimonials', delay: '24 hours' },
        { day: 2, type: 'call', subject: 'Personal consultation call', delay: '48 hours' },
        { day: 4, type: 'email', subject: 'Flexible payment options', delay: '96 hours' },
        { day: 7, type: 'email', subject: 'Risk-free trial offer', delay: '168 hours' }
      ]
    }
  ];

  useEffect(() => {
    loadAutomationData();
  }, []);

  const loadAutomationData = () => {
    // Mock automation rules - in real implementation, fetch from Supabase
    setAutomationRules([
      {
        id: 1,
        name: 'Hot Lead Auto-Call',
        trigger: 'Lead score > 80',
        action: 'Schedule immediate call',
        status: 'active',
        triggered: 23,
        converted: 8,
        rate: 34.8
      },
      {
        id: 2,
        name: 'Demo No-Show Follow-up',
        trigger: 'Demo missed',
        action: 'Send email + SMS + reschedule',
        status: 'active',
        triggered: 12,
        converted: 5,
        rate: 41.7
      },
      {
        id: 3,
        name: 'Price Page Visitor',
        trigger: 'Pricing page view >3 times',
        action: 'Urgent email sequence',
        status: 'active',
        triggered: 18,
        converted: 4,
        rate: 22.2
      }
    ]);

    // Mock active automations
    setActiveAutomations([
      {
        id: 'seq-001',
        name: 'Q1 Email Sequence',
        type: 'email-sequence',
        leads: 156,
        status: 'running',
        currentStep: 'Step 3 of 7',
        startDate: '2025-01-01',
        completionRate: 67,
        conversionRate: 24
      },
      {
        id: 'urg-002',
        name: 'Urgency Closer - Hot Leads',
        type: 'urgency-closer',
        leads: 42,
        status: 'running',
        currentStep: 'Step 2 of 4',
        startDate: '2025-01-05',
        completionRate: 83,
        conversionRate: 45
      }
    ]);
  };

  const getWorkflowColor = (type) => {
    const colors = {
      'email': 'bg-blue-100 text-blue-800 border-blue-200',
      'multi-channel': 'bg-purple-100 text-purple-800 border-purple-200',
      'educational': 'bg-green-100 text-green-800 border-green-200',
      'intelligent': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors?.[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStepIcon = (type) => {
    const icons = {
      'email': Mail,
      'call': Phone,
      'sms': MessageCircle
    };
    return icons?.[type] || Mail;
  };

  const selectedWorkflowData = workflowTemplates?.find(w => w?.id === selectedWorkflow);

  return (
    <div className="space-y-6">
      {/* Automation Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Automations</p>
              <p className="text-3xl font-bold text-gray-900">8</p>
            </div>
            <Bot className="h-8 w-8 text-blue-600" />
          </div>
          <div className="mt-2 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            2 new this week
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Leads in Automation</p>
              <p className="text-3xl font-bold text-gray-900">342</p>
            </div>
            <Users className="h-8 w-8 text-green-600" />
          </div>
          <div className="mt-2 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            +15% this month
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Auto-Conversions</p>
              <p className="text-3xl font-bold text-gray-900">89</p>
            </div>
            <Target className="h-8 w-8 text-purple-600" />
          </div>
          <div className="mt-2 flex items-center text-sm text-green-600">
            <TrendingUp className="h-4 w-4 mr-1" />
            28% conversion rate
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Time Saved</p>
              <p className="text-3xl font-bold text-gray-900">127h</p>
            </div>
            <Clock className="h-8 w-8 text-orange-600" />
          </div>
          <div className="mt-2 flex items-center text-sm text-gray-600">
            <Clock className="h-4 w-4 mr-1" />
            This month
          </div>
        </motion.div>
      </div>
      {/* Workflow Templates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Automation Workflows</h3>
            <p className="text-gray-600 mt-1">Choose from pre-built templates or create custom automation sequences</p>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            New Workflow
          </button>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {workflowTemplates?.map((workflow) => (
              <motion.div
                key={workflow?.id}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedWorkflow(workflow?.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedWorkflow === workflow?.id
                    ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{workflow?.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{workflow?.description}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getWorkflowColor(workflow?.type)}`}>
                    {workflow?.type}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {workflow?.duration}
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    {workflow?.steps} steps
                  </div>
                  <div className="flex items-center text-green-600 font-medium">
                    <TrendingUp className="h-4 w-4 mr-1" />
                    {workflow?.successRate}%
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1">
                  {workflow?.triggers?.slice(0, 2)?.map((trigger, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                      {trigger}
                    </span>
                  ))}
                  {workflow?.triggers?.length > 2 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                      +{workflow?.triggers?.length - 2} more
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      {/* Selected Workflow Details */}
      {selectedWorkflowData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Workflow Steps */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900">{selectedWorkflowData?.name}</h4>
              <p className="text-gray-600 mt-1">Detailed step-by-step workflow breakdown</p>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                {selectedWorkflowData?.steps_detail?.map((step, index) => {
                  const StepIcon = getStepIcon(step?.type);
                  return (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                        <StepIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <h5 className="text-sm font-medium text-gray-900">{step?.subject}</h5>
                          <span className="text-xs text-gray-500">Day {step?.day}</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {step?.type?.toUpperCase()} • Delay: {step?.delay}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 flex space-x-3">
                <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center">
                  <Play className="h-4 w-4 mr-2" />
                  Launch Workflow
                </button>
                <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center">
                  <Copy className="h-4 w-4 mr-2" />
                  Clone
                </button>
                <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center">
                  <Edit3 className="h-4 w-4 mr-2" />
                  Customize
                </button>
              </div>
            </div>
          </div>

          {/* Workflow Performance */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h4 className="text-lg font-semibold text-gray-900">Performance Metrics</h4>
              <p className="text-gray-600 mt-1">Historical performance data for this workflow type</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-600">Success Rate</span>
                    <span className="text-2xl font-bold text-green-900">
                      {selectedWorkflowData?.successRate}%
                    </span>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-600">Avg Duration</span>
                    <span className="text-2xl font-bold text-blue-900">
                      {selectedWorkflowData?.duration}
                    </span>
                  </div>
                </div>
              </div>

              {/* Triggers */}
              <div>
                <h5 className="font-medium text-gray-900 mb-3">Trigger Conditions</h5>
                <div className="space-y-2">
                  {selectedWorkflowData?.triggers?.map((trigger, index) => (
                    <div key={index} className="flex items-center p-2 bg-gray-50 rounded">
                      <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                      <span className="text-sm text-gray-700">{trigger}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Best Practices */}
              <div>
                <h5 className="font-medium text-gray-900 mb-3">Best Practices</h5>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Test different subject lines for higher open rates
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Personalize based on lead score and engagement history
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                    Monitor unsubscribe rates and adjust frequency
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Active Automations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Active Automations</h3>
          <p className="text-gray-600 mt-1">Currently running automation workflows</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Automation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeAutomations?.map((automation) => (
                <tr key={automation?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{automation?.name}</div>
                      <div className="text-sm text-gray-500">{automation?.currentStep}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      automation?.status === 'running' ?'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                    }`}>
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        automation?.status === 'running' ? 'bg-green-400' : 'bg-gray-400'
                      }`}></span>
                      {automation?.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {automation?.leads}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${automation?.completionRate}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">{automation?.completionRate}% complete</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <span className="text-green-600 font-medium">{automation?.conversionRate}%</span>
                      <TrendingUp className="h-4 w-4 ml-1 text-green-500" />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button className="text-blue-600 hover:text-blue-900 flex items-center">
                      <BarChart3 className="h-4 w-4 mr-1" />
                      View Details
                    </button>
                    <button className="text-gray-600 hover:text-gray-900 flex items-center">
                      <Settings className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button className="text-red-600 hover:text-red-900 flex items-center">
                      <Pause className="h-4 w-4 mr-1" />
                      Stop
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Automation Rules */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Automation Rules</h3>
            <p className="text-gray-600 mt-1">Smart triggers that automatically initiate actions based on lead behavior</p>
          </div>
          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Add Rule
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {automationRules?.map((rule) => (
            <motion.div
              key={rule?.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-4 ${
                  rule?.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                }`}></div>
                <div>
                  <h4 className="font-medium text-gray-900">{rule?.name}</h4>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">When:</span> {rule?.trigger} → 
                    <span className="font-medium"> Then:</span> {rule?.action}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">{rule?.triggered}</div>
                  <div className="text-xs text-gray-500">Triggered</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-600">{rule?.converted}</div>
                  <div className="text-xs text-gray-500">Converted</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-600">{rule?.rate}%</div>
                  <div className="text-xs text-gray-500">Rate</div>
                </div>
                
                <div className="flex space-x-2">
                  <button className="p-2 text-gray-600 hover:text-gray-800">
                    <Settings className="h-4 w-4" />
                  </button>
                  <button className={`p-2 ${
                    rule?.status === 'active' ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'
                  }`}>
                    {rule?.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AutomationPanel;